import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { WifiOffIcon, ShieldAlertIcon, UsersIcon, HeartOffIcon } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0,
    x: 30
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15,
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
export function SlideOne() {
  return <motion.div className="w-full h-full overflow-y-auto" variants={container} initial="hidden" animate="visible" exit={{
    opacity: 0,
    x: -30,
    transition: {
      duration: 0.3
    }
  }} data-id="element-14">
      <motion.div variants={item} className="flex justify-between items-center w-full fixed top-6 left-0 px-16 text-[11px] font-medium tracking-[0.2em] text-apple-muted uppercase z-10" data-id="element-15">
        <span data-id="element-16">
          <span className="text-apple-accent" data-id="element-17">01</span>
          <span className="opacity-50" data-id="element-18"> / 02</span>
        </span>
        <span data-id="element-19">The design challenge</span>
      </motion.div>

      <div className="w-full max-w-[1120px] mx-auto px-16 pt-28 pb-24 flex flex-col gap-12" data-id="element-20">
        {/* Hero */}
        <motion.div variants={item} className="flex flex-col gap-2" data-id="element-21">
          <span className="text-apple-accent text-xs font-semibold tracking-[0.2em] uppercase mb-2" data-id="element-22">
            The Design Challenge
          </span>
          <h1 className="text-[clamp(36px,4.5vw,64px)] leading-[1.08] font-semibold tracking-tight text-apple-text max-w-[900px]" data-id="element-23">
            The platform won't solve the problem{' '}
            <span className="text-apple-accent italic font-medium" data-id="element-24">
              unless we ask the right question first
            </span>
          </h1>
        </motion.div>

        {/* Side-by-side: Brief vs Research */}
        <motion.div variants={item} className="relative grid grid-cols-2 gap-8" data-id="element-25">
          {/* VS Badge */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-black/5 rounded-full flex items-center justify-center z-10 border border-apple-border shadow-sm" data-id="element-26">
            <span className="text-apple-muted text-xs font-medium" data-id="element-27">vs</span>
          </div>

          {/* Left Card: The Brief */}
          <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-8 flex flex-col gap-5" data-id="element-28">
            <h3 className="text-xs font-semibold tracking-[0.15em] text-apple-muted uppercase" data-id="element-29">
              The client brief
            </h3>
            <p className="text-sm text-apple-text leading-relaxed italic" data-id="element-30">
              "A government department wants to launch a digital service to
              reduce social isolation in older adults in remote areas. The goal
              is to build an online community platform where people can join
              virtual meetups and find local events."
            </p>
          </div>

          {/* Right Card: Research */}
          <div className="bg-apple-card shadow-sm border border-apple-border/50 ring-1 ring-apple-accent/20 rounded-2xl p-8 flex flex-col gap-5" data-id="element-31">
            <h3 className="text-xs font-semibold tracking-[0.15em] text-apple-accent uppercase" data-id="element-32">
              What research tells us
            </h3>
            <ul className="flex flex-col gap-3.5" data-id="element-33">
              {['Many have no reliable internet access', 'Strong preference for in-person contact', 'Trust flows through existing community groups', 'Digital exclusion is the barrier, not the solution'].map((text, i) => <li key={i} className="flex items-center gap-3" data-id="element-34">
                  <div className="w-1.5 h-1.5 rounded-full bg-apple-accent shrink-0" data-id="element-35" />
                  <span className="text-sm text-apple-text" data-id="element-36">{text}</span>
                </li>)}
            </ul>
          </div>
        </motion.div>

        {/* Strikethrough Callout */}
        <motion.div variants={item} className="pl-6 border-l-[3px] border-apple-accent" data-id="element-37">
          <p className="text-lg text-apple-muted font-light leading-relaxed" data-id="element-38">
            The goal is not to{' '}
            <span className="line-through opacity-40" data-id="element-39">
              build a digital platform
            </span>
            . The goal is to{' '}
            <strong className="text-apple-text font-semibold" data-id="element-40">
              reduce isolation in hard-to-reach populations
            </strong>
            .
          </p>
        </motion.div>

        {/* Evidence Cards 2x2 */}
        <motion.div variants={item} className="grid grid-cols-2 gap-5" data-id="element-41">
          <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-7 flex flex-col gap-3" data-id="element-42">
            <WifiOffIcon className="text-apple-accent" size={20} data-id="element-43" />
            <h3 className="text-sm font-semibold text-apple-text leading-snug" data-id="element-44">
              Digital exclusion is real and significant
            </h3>
            <p className="text-xs text-apple-muted leading-relaxed flex-grow" data-id="element-45">
              A large proportion of older adults are still excluded from digital
              services, and this is strongly linked to loneliness.
            </p>
            <span className="text-[10px] text-apple-muted/50 uppercase tracking-wider font-medium pt-2" data-id="element-46">
              Source: BMC Geriatrics
            </span>
          </div>

          <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-7 flex flex-col gap-3" data-id="element-47">
            <ShieldAlertIcon className="text-apple-accent" size={20} data-id="element-48" />
            <h3 className="text-sm font-semibold text-apple-text leading-snug" data-id="element-49">
              Access isn't the only problem
            </h3>
            <p className="text-xs text-apple-muted leading-relaxed flex-grow" data-id="element-50">
              Confidence, usability, and trust are bigger barriers than access
              alone. Many interventions fail because tools are not designed
              around older users' needs.
            </p>
            <span className="text-[10px] text-apple-muted/50 uppercase tracking-wider font-medium pt-2" data-id="element-51">
              Source: arXiv
            </span>
          </div>

          <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-7 flex flex-col gap-3" data-id="element-52">
            <UsersIcon className="text-apple-accent" size={20} data-id="element-53" />
            <h3 className="text-sm font-semibold text-apple-text leading-snug" data-id="element-54">
              Technology needs human systems
            </h3>
            <p className="text-xs text-apple-muted leading-relaxed flex-grow" data-id="element-55">
              Social care providers and trusted intermediaries play a key role
              in adoption.
            </p>
            <span className="text-[10px] text-apple-muted/50 uppercase tracking-wider font-medium pt-2" data-id="element-56">
              Source: Springer Link
            </span>
          </div>

          <div className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl p-7 flex flex-col gap-3" data-id="element-57">
            <HeartOffIcon className="text-apple-accent" size={20} data-id="element-58" />
            <h3 className="text-sm font-semibold text-apple-text leading-snug" data-id="element-59">
              Digital-only excludes the most vulnerable
            </h3>
            <p className="text-xs text-apple-muted leading-relaxed flex-grow" data-id="element-60">
              Older adults still prefer offline or hybrid support models.
              Digital-only solutions risk excluding the most vulnerable.
            </p>
            <span className="text-[10px] text-apple-muted/50 uppercase tracking-wider font-medium pt-2" data-id="element-61">
              Source: BMC Public Health
            </span>
          </div>
        </motion.div>

        {/* Bottom Reframing Question */}
        <motion.div variants={item} className="bg-apple-card shadow-sm border border-apple-border/50 rounded-2xl px-8 py-6 pl-6 border-l-[4px] border-l-apple-accent" data-id="element-62">
          <p className="text-lg text-apple-text leading-relaxed" data-id="element-63">
            <span className="text-apple-accent font-semibold mr-2" data-id="element-64">
              The real design question:
            </span>
            <span className="italic font-medium" data-id="element-65">
              How do we reduce isolation for people who can't or won't use a
              digital platform, while still delivering the service the client
              needs to launch?
            </span>
          </p>
        </motion.div>
      </div>
    </motion.div>;
}